/**
 * Native (Capacitor) app initialization — a NO-OP on web. Call once on startup.
 *
 * - Hides the splash screen once the web app is ready.
 * - Sets the status bar style.
 * - Wires the Android hardware back button (history back, or exit at root).
 * - Registers for push notifications and stores the device token in Supabase
 *   (push_tokens table) so the backend can target it once FCM send is wired.
 *   A notification tap dispatches a window `openChatConversation` event carrying
 *   the conversation id (listen for it to deep-link into the chat).
 *
 * NOTE: push DELIVERY requires a Firebase project (google-services.json in the
 * Android app) + an FCM send path from the backend — see CAPACITOR_SETUP.md.
 */
import { Capacitor } from '@capacitor/core';
import { supabase } from '../components/data/supabaseClient';
import { runTopBackHandler } from './backHandler';

export async function initCapacitor() {
  if (!Capacitor.isNativePlatform || !Capacitor.isNativePlatform()) return;

  // Marker for native-only CSS (e.g. full-screen chat sheet, keyboard layout).
  try {
    document.documentElement.classList.add('capacitor-native');
    document.documentElement.classList.add(`capacitor-${Capacitor.getPlatform()}`);
  } catch (e) { /* non-DOM env */ }

  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) { /* plugin optional */ }

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    // The app bar at the top is light, so the status-bar icons must be dark.
    // (Capacitor Style.Light = light background → dark icons/text.)
    await StatusBar.setStyle({ style: Style.Light });
    try { await StatusBar.setBackgroundColor({ color: '#eef3ec' }); } catch (e) { /* iOS/overlay */ }
  } catch (e) { /* status bar optional */ }

  try {
    const { App } = await import('@capacitor/app');
    App.addListener('backButton', ({ canGoBack }) => {
      // Close an open overlay (chat, modal) first, if any registered a handler.
      if (runTopBackHandler()) return;
      // Root screens (login / home): exit the app instead of walking history —
      // history.back() there just flashes the previous route before a redirect
      // bounces back, which looked like a glitching header.
      const path = (window.location && window.location.pathname) || '';
      if (path === '/login' || path === '/' || path === '/home') {
        App.exitApp();
        return;
      }
      if (canGoBack) window.history.back();
      else App.exitApp();
    });
  } catch (e) { /* app plugin optional */ }

  // Push is OFF until Firebase (google-services.json) is configured for the
  // app.WayShare package. Registering without it crashes natively the moment
  // the user grants notification permission, so don't even init/ask. Re-enable
  // by adding google-services.json for app.WayShare and setting
  // REACT_APP_PUSH_ENABLED=true at build time.
  if (process.env.REACT_APP_PUSH_ENABLED === 'true') {
    await initPush();
  }
}

async function initPush() {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    PushNotifications.addListener('registration', (token) => {
      savePushToken(token && token.value);
    });
    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error:', err);
    });
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = (action && action.notification && action.notification.data) || {};
      if (data.conversation_id) {
        window.dispatchEvent(
          new CustomEvent('openChatConversation', { detail: { conversationId: data.conversation_id } })
        );
      }
    });

    await PushNotifications.register();
  } catch (e) {
    console.error('initPush failed:', e);
  }
}

async function savePushToken(token) {
  if (!token) return;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from('push_tokens').upsert(
      {
        token,
        user_id: user.id,
        platform: Capacitor.getPlatform(),
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'token' }
    );
  } catch (e) {
    console.error('savePushToken failed:', e);
  }
}
